import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTimezone } from '../context/TimezoneContext';
import {
  disable2Fa,
  getAssetUrl,
  getCurrentUser,
  getSessions,
  regenerateBackupCodes,
  revokeAllSessions,
  revokeSession,
  setup2Fa,
  updateTenantSettings,
  getTenantSettings,
  getTenantPlan,
  getTenantTrialStatus,
  uploadTenantLogo,
  deleteTenantLogo,
  type TwoFaSetupResponse,
  type UserSession,
  verify2FaSetup,
} from '../services/api';
import type { TenantPlan, TenantTrialStatus } from '../types';

const LOGO_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

const AFRICAN_TIMEZONES = [
  { value: 'Africa/Lagos', label: 'West Africa Time (WAT) - Lagos, Accra' },
  { value: 'Africa/Kinshasa', label: 'West Africa Time (WAT) - Kinshasa, Luanda' },
  { value: 'Africa/Nairobi', label: 'East Africa Time (EAT) - Nairobi, Kampala' },
  { value: 'Africa/Addis_Ababa', label: 'East Africa Time (EAT) - Addis Ababa' },
  { value: 'Africa/Harare', label: 'Central Africa Time (CAT) - Harare, Lusaka' },
  { value: 'Africa/Johannesburg', label: 'South Africa Standard Time (SAST) - Johannesburg, Cape Town' },
  { value: 'Africa/Cairo', label: 'Eastern European Time (EET) - Cairo' },
  { value: 'Africa/Casablanca', label: 'Morocco Time - Casablanca' },
  { value: 'UTC', label: 'UTC / GMT' },
];

type Tab = 'general' | 'branding' | 'security';

function normalizeEditableNumber(value: string, fallback: number) {
  if (value.trim() === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function waitForLogoImage(src: string) {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Logo preview failed to load'));
    image.src = src;
  });
}

export default function Settings() {
  const { timezone, setTimezone, heartbeatTimeout, setHeartbeatTimeout } = useTimezone();
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedTz, setSelectedTz] = useState(timezone);
  const [timeoutMins, setTimeoutMins] = useState(String(heartbeatTimeout));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [message, setMessage] = useState('');
  const [tenantPlan, setTenantPlan] = useState<TenantPlan | null>(null);
  const [trialStatus, setTrialStatus] = useState<TenantTrialStatus | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoLocalPreviewUrl, setLogoLocalPreviewUrl] = useState<string | null>(null);
  const [logoImageError, setLogoImageError] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [brandingEnabled, setBrandingEnabled] = useState(false);
  const [platformName, setPlatformName] = useState('');
  const [platformShortName, setPlatformShortName] = useState('');
  const [platformDescription, setPlatformDescription] = useState('');
  const [initialBranding, setInitialBranding] = useState({
    brandingEnabled: false,
    platformName: '',
    platformShortName: '',
    platformDescription: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null);
  const [twoFaSetup, setTwoFaSetup] = useState<TwoFaSetupResponse | null>(null);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [disableTwoFaCode, setDisableTwoFaCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionActionId, setSessionActionId] = useState<string | null>(null);
  const [securityNotice, setSecurityNotice] = useState('');
  const [securityError, setSecurityError] = useState('');

  useEffect(() => {
    getTenantSettings()
      .then((settings) => {
        if (settings.timezone) {
          setSelectedTz(settings.timezone);
        }
        if (settings.heartbeat_timeout_minutes) {
          setTimeoutMins(String(settings.heartbeat_timeout_minutes));
        }
        if (settings.logo_url) {
          setLogoUrl(getAssetUrl(settings.logo_url));
          setLogoImageError(false);
        }
        const nextBranding = {
          brandingEnabled: settings.branding_enabled === true,
          platformName: settings.platform_name || '',
          platformShortName: settings.platform_short_name || '',
          platformDescription: settings.platform_description || '',
        };
        setBrandingEnabled(nextBranding.brandingEnabled);
        setPlatformName(nextBranding.platformName);
        setPlatformShortName(nextBranding.platformShortName);
        setPlatformDescription(nextBranding.platformDescription);
        setInitialBranding(nextBranding);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.allSettled([getTenantPlan(), getTenantTrialStatus()])
      .then(([planResult, trialResult]) => {
        if (planResult.status === 'fulfilled') {
          setTenantPlan(planResult.value);
        }
        if (trialResult.status === 'fulfilled') {
          setTrialStatus(trialResult.value);
        }
      })
      .finally(() => setPlanLoading(false));
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        if (typeof user.require_2fa === 'boolean') {
          setTwoFaEnabled(user.require_2fa);
        }
      })
      .catch(() => {});
    loadSessions();
  }, []);

  useEffect(() => {
    if (!message && !saved) return;
    const timer = setTimeout(() => {
      setMessage('');
      setSaved(false);
    }, 3500);
    return () => clearTimeout(timer);
  }, [message, saved]);

  useEffect(() => {
    return () => {
      if (logoLocalPreviewUrl) {
        URL.revokeObjectURL(logoLocalPreviewUrl);
      }
    };
  }, [logoLocalPreviewUrl]);

  const selectedTimezone = useMemo(
    () => AFRICAN_TIMEZONES.find((item) => item.value === selectedTz),
    [selectedTz]
  );
  const previewTime = useMemo(
    () =>
      new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'medium',
        timeZone: selectedTz,
      }),
    [selectedTz]
  );
  const normalizedTimeoutMins = normalizeEditableNumber(timeoutMins, heartbeatTimeout);
  const timeoutDisplay = timeoutMins || String(normalizedTimeoutMins);
  const hasGeneralChanges = selectedTz !== timezone || normalizedTimeoutMins !== heartbeatTimeout;
  const planSummary = useMemo(
    () => getSettingsPlanSummary(tenantPlan, trialStatus, planLoading),
    [planLoading, tenantPlan, trialStatus]
  );
  const trimmedBranding = useMemo(
    () => ({
      brandingEnabled,
      platformName: platformName.trim(),
      platformShortName: platformShortName.trim(),
      platformDescription: platformDescription.trim(),
    }),
    [brandingEnabled, platformDescription, platformName, platformShortName]
  );
  const hasBrandingChanges =
    trimmedBranding.brandingEnabled !== initialBranding.brandingEnabled ||
    trimmedBranding.platformName !== initialBranding.platformName ||
    trimmedBranding.platformShortName !== initialBranding.platformShortName ||
    trimmedBranding.platformDescription !== initialBranding.platformDescription;
  const visibleLogoUrl = logoLocalPreviewUrl || (logoImageError ? null : logoUrl);
  const activeSessionCount = sessions.filter((session) => !isExpiredSession(session.expires_at)).length;

  async function handleSave() {
    setSaving(true);
    setMessage('');
    try {
      await updateTenantSettings({ timezone: selectedTz, heartbeat_timeout_minutes: normalizedTimeoutMins });
      setTimezone(selectedTz);
      setHeartbeatTimeout(normalizedTimeoutMins);
      setTimeoutMins(String(normalizedTimeoutMins));
      setSaved(true);
    } catch {
      window.alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoError('');
    if (file.size > LOGO_UPLOAD_MAX_BYTES) {
      setLogoError('Logo must be smaller than 2 MB.');
      event.target.value = '';
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const allowedByExtension = /\.(png|jpe?g|svg)$/i.test(file.name);
    if (!allowed.includes(file.type) && !allowedByExtension) {
      setLogoError('Only PNG, JPG or SVG logos are allowed.');
      event.target.value = '';
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setLogoLocalPreviewUrl(localPreviewUrl);
    setLogoImageError(false);
    setLogoUploadProgress(8);
    setUploadingLogo(true);
    setMessage('');
    try {
      const result = await uploadTenantLogo(file, setLogoUploadProgress);
      if (result.logo_url) {
        await updateTenantSettings({ branding_enabled: true });
        const remoteLogoUrl = getAssetUrl(result.logo_url);
        setLogoUrl(remoteLogoUrl);
        setBrandingEnabled(true);
        setInitialBranding((current) => ({ ...current, brandingEnabled: true }));
        window.dispatchEvent(new CustomEvent('tenant:branding-updated', { detail: { logo_url: result.logo_url, branding_enabled: true } }));
        setLogoUploadProgress(100);
        if (remoteLogoUrl) {
          try {
            await waitForLogoImage(remoteLogoUrl);
            setLogoLocalPreviewUrl(null);
            setLogoImageError(false);
          } catch {
            setLogoError('Logo uploaded, but the saved preview could not load. The local preview will remain until you refresh.');
          }
        }
        setMessage('Workspace logo updated.');
      } else {
        setLogoError('Logo upload finished, but the server did not return a logo URL.');
      }
    } catch (err: any) {
      setLogoLocalPreviewUrl(null);
      setLogoImageError(false);
      setLogoError(err.response?.data?.error || 'Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
      setLogoUploadProgress(0);
      event.target.value = '';
    }
  }

  async function handleRemoveLogo() {
    if (!window.confirm('Use the default Seedronics brand for this workspace?')) return;
    setMessage('');
    setLogoError('');
    try {
      await deleteTenantLogo();
      await updateTenantSettings({ branding_enabled: false });
      setLogoUrl(null);
      setLogoLocalPreviewUrl(null);
      setLogoImageError(false);
      setBrandingEnabled(false);
      setInitialBranding((current) => ({ ...current, brandingEnabled: false }));
      window.dispatchEvent(new CustomEvent('tenant:branding-updated', { detail: { logo_url: null, branding_enabled: false } }));
      setMessage('Seedronics default branding restored.');
    } catch (err: any) {
      setLogoError(err.response?.data?.error || 'Failed to remove logo. Please try again.');
    }
  }

  async function handleSaveBranding(event: React.FormEvent) {
    event.preventDefault();
    setSavingBranding(true);
    setMessage('');
    const payload = {
      branding_enabled: trimmedBranding.brandingEnabled,
      platform_name: trimmedBranding.platformName || null,
      platform_short_name: trimmedBranding.platformShortName || null,
      platform_description: trimmedBranding.platformDescription || null,
    };
    try {
      const result = await updateTenantSettings(payload);
      const nextBranding = {
        brandingEnabled: result.branding_enabled === true,
        platformName: result.platform_name || '',
        platformShortName: result.platform_short_name || '',
        platformDescription: result.platform_description || '',
      };
      setBrandingEnabled(nextBranding.brandingEnabled);
      setPlatformName(nextBranding.platformName);
      setPlatformShortName(nextBranding.platformShortName);
      setPlatformDescription(nextBranding.platformDescription);
      setInitialBranding(nextBranding);
      window.dispatchEvent(new CustomEvent('tenant:branding-updated', {
        detail: {
          branding_enabled: nextBranding.brandingEnabled,
          platform_name: nextBranding.platformName || null,
          platform_short_name: nextBranding.platformShortName || null,
          platform_description: nextBranding.platformDescription || null,
        },
      }));
      setMessage(nextBranding.brandingEnabled ? 'Workspace branding saved.' : 'Seedronics default branding restored.');
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Failed to save branding. Please try again.');
    } finally {
      setSavingBranding(false);
    }
  }

  async function loadSessions() {
    setSessionsLoading(true);
    try {
      const data = await getSessions();
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to load active sessions.');
    } finally {
      setSessionsLoading(false);
    }
  }

  async function handleStartTwoFaSetup() {
    if (
      twoFaEnabled === null &&
      !window.confirm('Start 2FA setup? The current 2FA status could not be refreshed, so only continue from a trusted browser.')
    ) {
      return;
    }
    setTwoFaBusy(true);
    setSecurityError('');
    setSecurityNotice('');
    try {
      const data = await setup2Fa();
      setTwoFaSetup(data);
      setBackupCodes(data.backupCodes || []);
      setTwoFaEnabled(false);
      setSecurityNotice('Scan the QR code, save the backup codes, then enter a 6-digit authenticator code to enable 2FA.');
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to start 2FA setup.');
    } finally {
      setTwoFaBusy(false);
    }
  }

  async function handleVerifyTwoFaSetup(event: React.FormEvent) {
    event.preventDefault();
    setTwoFaBusy(true);
    setSecurityError('');
    setSecurityNotice('');
    try {
      await verify2FaSetup(twoFaCode.trim());
      setTwoFaEnabled(true);
      setTwoFaSetup(null);
      setTwoFaCode('');
      setSecurityNotice('Two-factor authentication is now enabled. Keep your backup codes somewhere safe.');
      await loadSessions();
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to verify the authenticator code.');
    } finally {
      setTwoFaBusy(false);
    }
  }

  async function handleRegenerateBackupCodes() {
    if (!window.confirm('Generate new backup codes? Old backup codes will stop working.')) return;
    setTwoFaBusy(true);
    setSecurityError('');
    setSecurityNotice('');
    try {
      const data = await regenerateBackupCodes();
      setBackupCodes(data.backupCodes || []);
      setTwoFaEnabled(true);
      setSecurityNotice('New backup codes generated. Save them now; they will not be shown again.');
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to regenerate backup codes.');
    } finally {
      setTwoFaBusy(false);
    }
  }

  async function handleDisableTwoFa(event: React.FormEvent) {
    event.preventDefault();
    if (!window.confirm('Disable two-factor authentication for this account? Other sessions will be revoked.')) return;
    setTwoFaBusy(true);
    setSecurityError('');
    setSecurityNotice('');
    try {
      await disable2Fa(disableTwoFaCode.trim());
      setTwoFaEnabled(false);
      setTwoFaSetup(null);
      setBackupCodes([]);
      setDisableTwoFaCode('');
      setSecurityNotice('Two-factor authentication has been disabled.');
      await loadSessions();
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to disable 2FA with that code.');
    } finally {
      setTwoFaBusy(false);
    }
  }

  async function handleRevokeSession(session: UserSession) {
    if (session.is_current) return;
    if (!window.confirm('Revoke this browser session?')) return;
    setSessionActionId(session.id);
    setSecurityError('');
    setSecurityNotice('');
    try {
      await revokeSession(session.id);
      setSecurityNotice('Session revoked.');
      await loadSessions();
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to revoke that session.');
    } finally {
      setSessionActionId(null);
    }
  }

  async function handleRevokeOtherSessions() {
    if (!window.confirm('Revoke all other active sessions for your account?')) return;
    setSessionActionId('all');
    setSecurityError('');
    setSecurityNotice('');
    try {
      const data = await revokeAllSessions();
      setSecurityNotice(`${data.revoked_count || 0} other session${data.revoked_count === 1 ? '' : 's'} revoked.`);
      await loadSessions();
    } catch (err: any) {
      setSecurityError(err.response?.data?.error || 'Unable to revoke other sessions.');
    } finally {
      setSessionActionId(null);
    }
  }

  const settingCards: {
    detail: string;
    icon: SettingsIconType;
    key: Tab;
    label: string;
    status: string;
    tone: 'success' | 'warning' | 'danger';
  }[] = [
    {
      detail: 'Timezone, heartbeat, and defaults.',
      icon: 'clock',
      key: 'general',
      label: 'General',
      status: hasGeneralChanges ? 'Unsaved changes' : 'Active',
      tone: hasGeneralChanges ? 'warning' : 'success',
    },
    {
      detail: 'Workspace identity and labels.',
      icon: 'brand',
      key: 'branding',
      label: 'Branding',
      status: brandingEnabled || logoUrl ? 'Configured' : 'Not configured',
      tone: brandingEnabled || logoUrl ? 'success' : 'warning',
    },
    {
      detail: 'Access, sessions, and protection.',
      icon: 'shield',
      key: 'security',
      label: 'Security',
      status: twoFaEnabled ? '2FA enabled' : `${activeSessionCount} active session${activeSessionCount === 1 ? '' : 's'}`,
      tone: twoFaEnabled ? 'success' : 'warning',
    },
  ];
  const activeSettingCard = settingCards.find((card) => card.key === activeTab) || settingCards[0];

  return (
    <section className="sd-page-stack sd-settings-page">
      <div className="sd-page-header sd-settings-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">ORGANIZATION</div>
          <h1>Settings</h1>
          <p>Configure tenant timezone, device heartbeat defaults, workspace branding, and account security.</p>
        </div>
        <div className="sd-page-actions sd-settings-save-top">
          <button className="sd-primary-button" disabled={!hasGeneralChanges || saving} onClick={handleSave} type="button">
            {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
          </button>
        </div>
      </div>

      {(saved || message) && (
        <div className="sd-team-message-stack">
          {saved && <div className="sd-toast is-success">Tenant settings saved.</div>}
          {message && <div className="sd-toast is-success">{message}</div>}
        </div>
      )}

      <div className="sd-settings-admin-grid" role="tablist" aria-label="Settings sections">
        {settingCards.map((tab) => (
          <button
            aria-selected={activeTab === tab.key}
            className={`sd-settings-admin-card ${activeTab === tab.key ? 'is-active' : ''}`}
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            type="button"
          >
            <span className={`sd-settings-admin-icon is-${tab.icon}`}>
              <SettingsIcon type={tab.icon} />
            </span>
            <span className="sd-settings-admin-copy">
              <strong>{tab.label}</strong>
              <small>{tab.detail}</small>
              <em className={`is-${tab.tone}`}>
                <i />
                {tab.status}
              </em>
            </span>
            <span className="sd-settings-card-arrow" aria-hidden="true" />
          </button>
        ))}
      </div>

      <section className="sd-settings-workspace">
        <article className="sd-panel sd-settings-main-panel">
          <div className="sd-panel-header">
            <div className="sd-settings-section-title">
              <span className={`sd-settings-admin-icon is-${activeSettingCard.icon}`}>
                <SettingsIcon type={activeSettingCard.icon} />
              </span>
              <div>
              <h2>{activeTab === 'general' ? 'General Preferences' : activeTab === 'branding' ? 'Branding' : 'Security Controls'}</h2>
              <p>
                {activeTab === 'general'
                  ? 'Set timestamp behavior and default offline detection for newly created devices.'
                  : activeTab === 'branding'
                    ? 'Manage the workspace logo used in the dashboard shell and generated reports.'
                    : 'Protect your sign-in with an authenticator app and manage active browser sessions.'}
              </p>
              </div>
            </div>
          </div>

          {activeTab === 'general' && (
            <div className="sd-settings-form-body">
              <section className={`sd-settings-plan-card is-${planSummary.status}`}>
                <div>
                  <span>Current plan</span>
                  <strong>{planSummary.label}</strong>
                  <p>{planSummary.detail}</p>
                </div>
                <div>
                  <span>{planSummary.metaLabel}</span>
                  <strong>{planSummary.metaValue}</strong>
                </div>
              </section>

              <label className="sd-field">
                <span>Timezone</span>
                <select value={selectedTz} onChange={(event) => setSelectedTz(event.target.value)}>
                  {AFRICAN_TIMEZONES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <small>Timestamp display zone for dashboards, alerts, audit logs, and PDF reports.</small>
              </label>

              <div className="sd-settings-preview-card">
                <span>Current Preview</span>
                <strong>{previewTime}</strong>
                <small>Dashboards, alerts, audit logs, and reports display timestamps in this timezone.</small>
              </div>

              <label className="sd-field sd-settings-number-field">
                <span>Default Heartbeat Timeout</span>
                <input
                  placeholder="e.g. 30"
                  type="number"
                  min={1}
                  max={1440}
                  value={timeoutMins}
                  onChange={(event) => setTimeoutMins(event.target.value)}
                />
                <small>Devices without telemetry inside this window are marked offline. Existing devices can override it individually.</small>
              </label>

              <div className="sd-settings-actions">
                <button className="sd-primary-button" disabled={!hasGeneralChanges || saving} onClick={handleSave} type="button">
                  {saving ? 'Saving...' : saved ? 'Saved' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="sd-settings-form-body">
              <form className="sd-branding-form" onSubmit={handleSaveBranding}>
                <label className="sd-branding-toggle">
                  <input
                    checked={brandingEnabled}
                    onChange={(event) => setBrandingEnabled(event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    <strong>Use custom workspace branding</strong>
                    <small>
                      Turn this off to show the default Seedronics brand while keeping uploaded logo and text saved.
                    </small>
                  </span>
                </label>

                <div className="sd-form-grid">
                  <label className="sd-field">
                    <span>Platform name</span>
                    <input
                      maxLength={100}
                      onChange={(event) => setPlatformName(event.target.value)}
                      placeholder="Your company or platform name"
                      type="text"
                      value={platformName}
                    />
                    <small>Shown beside the logo in the dashboard sidebar.</small>
                  </label>

                  <label className="sd-field">
                    <span>Short label</span>
                    <input
                      maxLength={30}
                      onChange={(event) => setPlatformShortName(event.target.value)}
                      placeholder="Cloud IoT"
                      type="text"
                      value={platformShortName}
                    />
                    <small>Small uppercase label under the platform name.</small>
                  </label>
                </div>

                <label className="sd-field">
                  <span>Description</span>
                  <textarea
                    maxLength={500}
                    onChange={(event) => setPlatformDescription(event.target.value)}
                    placeholder="Short description for this workspace brand, support context, or customer-facing identity."
                    rows={3}
                    value={platformDescription}
                  />
                  <small>{platformDescription.length}/500 characters. Saved for white-label context and future branded surfaces.</small>
                </label>

                <div className="sd-branding-preview">
                  <span>Dashboard preview</span>
                  <div className="sd-brand is-preview">
                    <div className={`sd-brand-mark ${logoUrl && brandingEnabled ? 'has-logo' : ''}`}>
                      {logoUrl && brandingEnabled ? <img src={logoUrl} alt="Brand logo preview" /> : 'S'}
                    </div>
                    <div>
                      <div className="sd-brand-name">{brandingEnabled ? trimmedBranding.platformName || 'Workspace name' : 'Seedronics'}</div>
                      <div className="sd-brand-subtitle">{brandingEnabled ? trimmedBranding.platformShortName || 'Cloud IoT' : 'Cloud IoT'}</div>
                    </div>
                  </div>
                </div>

                <div className="sd-settings-actions">
                  <button className="sd-primary-button" disabled={!hasBrandingChanges || savingBranding} type="submit">
                    {savingBranding ? 'Saving...' : 'Save Branding'}
                  </button>
                </div>
              </form>

              <div className="sd-settings-logo-card">
                <div className={`sd-settings-logo-preview ${uploadingLogo ? 'is-uploading' : ''}`}>
                  {visibleLogoUrl ? (
                    <img
                      src={visibleLogoUrl}
                      alt="Tenant logo"
                      onError={() => {
                        setLogoImageError(true);
                        if (!logoLocalPreviewUrl) {
                          setLogoError('The saved logo could not be displayed. Try replacing it, or ask support to check logo file serving.');
                        }
                      }}
                      onLoad={() => setLogoImageError(false)}
                    />
                  ) : (
                    <div className={logoImageError ? 'sd-logo-placeholder is-error' : 'sd-logo-placeholder'}>
                      <span>{logoImageError ? '!' : 'S'}</span>
                      <strong>{logoImageError ? 'Preview unavailable' : 'No Logo'}</strong>
                    </div>
                  )}
                  {uploadingLogo && (
                    <div className="sd-logo-upload-state" role="status" aria-live="polite">
                      <span>{logoUploadProgress >= 95 ? 'Finalizing logo...' : `Uploading logo ${logoUploadProgress}%`}</span>
                      <div className="sd-logo-progress-track" aria-hidden="true">
                        <div
                          className="sd-logo-progress-bar"
                          style={{ width: `${Math.max(8, logoUploadProgress)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="sd-settings-logo-copy">
                  <span>Workspace Logo</span>
                  <strong>{logoUrl ? 'Tenant logo uploaded' : 'Seedronics default branding'}</strong>
                  <p>
                    Use a PNG, JPG, or SVG with a transparent background. The logo appears in the dashboard for
                    white-label-enabled workspaces and in generated PDF reports. Maximum size is 2 MB.
                  </p>
                  <div className="sd-settings-logo-actions">
                    <input
                      ref={logoInputRef}
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                      className="sd-logo-file-input"
                      disabled={uploadingLogo}
                      onChange={handleLogoChange}
                      type="file"
                    />
                    <button
                      className="sd-primary-button"
                      disabled={uploadingLogo}
                      onClick={() => logoInputRef.current?.click()}
                      type="button"
                    >
                      {uploadingLogo ? 'Uploading...' : logoUrl ? 'Replace Logo' : 'Upload Logo'}
                    </button>
                    {logoUrl && (
                      <button className="sd-settings-danger-button" disabled={uploadingLogo} onClick={handleRemoveLogo} type="button">
                        Use Seedronics Default
                      </button>
                    )}
                  </div>
                  {logoError && <div className="sd-inline-alert is-danger sd-logo-upload-alert">{logoError}</div>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="sd-settings-form-body">
              {(securityNotice || securityError) && (
                <div className={`sd-inline-alert ${securityError ? 'is-warning' : ''}`}>
                  {securityError || securityNotice}
                </div>
              )}

              <section className="sd-security-card">
                <div className="sd-security-card-header">
                  <div>
                    <span>Two-factor authentication</span>
                    <strong>{twoFaEnabled ? 'Enabled' : twoFaEnabled === false ? 'Not enabled' : 'Status unavailable'}</strong>
                    <p>
                      Use an authenticator app or backup code as a second step when signing in.
                    </p>
                  </div>
                  {twoFaEnabled !== true && (
                    <button className="sd-primary-button" disabled={twoFaBusy} onClick={handleStartTwoFaSetup} type="button">
                      {twoFaBusy ? 'Starting...' : twoFaSetup ? 'Restart setup' : 'Set up 2FA'}
                    </button>
                  )}
                </div>

                {twoFaEnabled === null && (
                  <p className="sd-security-note">
                    We could not refresh your current 2FA status. Use Refresh or try again after confirming your session is still active.
                  </p>
                )}

                {twoFaSetup && (
                  <div className="sd-security-setup-grid">
                    <div className="sd-security-qr">
                      <QRCodeCanvas value={twoFaSetup.otpauthUrl} size={180} includeMargin />
                      <small>Scan with Google Authenticator, Microsoft Authenticator, 1Password, or a compatible app.</small>
                    </div>
                    <div className="sd-security-secret">
                      <span>Manual setup key</span>
                      <code>{twoFaSetup.secret}</code>
                      <form onSubmit={handleVerifyTwoFaSetup}>
                        <label className="sd-field">
                          <span>Authenticator code</span>
                          <input
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            maxLength={6}
                            onChange={(event) => setTwoFaCode(event.target.value)}
                            placeholder="123456"
                            required
                            type="text"
                            value={twoFaCode}
                          />
                          <small>Enter the 6-digit code from your authenticator app to finish setup.</small>
                        </label>
                        <button className="sd-primary-button" disabled={twoFaBusy || twoFaCode.trim().length !== 6} type="submit">
                          {twoFaBusy ? 'Verifying...' : 'Enable 2FA'}
                        </button>
                      </form>
                    </div>
                  </div>
                )}

                {(backupCodes.length > 0 || twoFaEnabled === true) && (
                  <div className="sd-security-backup-block">
                    <div>
                      <span>Backup codes</span>
                      <p>Each code can be used once if the authenticator app is unavailable.</p>
                    </div>
                    {backupCodes.length > 0 && (
                      <div className="sd-security-code-grid">
                        {backupCodes.map((code) => (
                          <code key={code}>{code}</code>
                        ))}
                      </div>
                    )}
                    {twoFaEnabled === true && (
                      <button className="sd-secondary-button" disabled={twoFaBusy} onClick={handleRegenerateBackupCodes} type="button">
                        {twoFaBusy ? 'Generating...' : 'Generate new backup codes'}
                      </button>
                    )}
                  </div>
                )}

                {twoFaEnabled === true && (
                  <form className="sd-security-disable-form" onSubmit={handleDisableTwoFa}>
                    <label className="sd-field">
                      <span>Disable 2FA</span>
                      <input
                        autoComplete="one-time-code"
                        onChange={(event) => setDisableTwoFaCode(event.target.value)}
                        placeholder="Authenticator or backup code"
                        required
                        type="text"
                        value={disableTwoFaCode}
                      />
                      <small>Disabling 2FA revokes other sessions and should only be done from a trusted device.</small>
                    </label>
                    <button className="sd-settings-danger-button" disabled={twoFaBusy || disableTwoFaCode.trim().length < 6} type="submit">
                      {twoFaBusy ? 'Disabling...' : 'Disable 2FA'}
                    </button>
                  </form>
                )}
              </section>

              <section className="sd-security-card">
                <div className="sd-security-card-header">
                  <div>
                    <span>Active sessions</span>
                    <strong>{sessionsLoading ? 'Loading...' : `${activeSessionCount} active`}</strong>
                    <p>Review where your Seedronics account is signed in and revoke sessions you do not recognize.</p>
                  </div>
                  <button className="sd-secondary-button" disabled={sessionsLoading} onClick={loadSessions} type="button">
                    Refresh
                  </button>
                </div>

                <div className="sd-security-session-actions">
                  <button
                    className="sd-settings-danger-button"
                    disabled={sessionActionId === 'all' || activeSessionCount <= 1}
                    onClick={handleRevokeOtherSessions}
                    type="button"
                  >
                    {sessionActionId === 'all' ? 'Revoking...' : 'Revoke other sessions'}
                  </button>
                </div>

                <div className="sd-security-session-list">
                  {sessions.length === 0 && (
                    <div className="sd-settings-preview-card">
                      <strong>{sessionsLoading ? 'Loading sessions...' : 'No active sessions found'}</strong>
                    </div>
                  )}
                  {sessions.map((session) => (
                    <article className="sd-security-session" key={session.id}>
                      <div>
                        <strong>{session.is_current ? 'Current session' : 'Browser session'}</strong>
                        <span>{session.ip_address || 'Unknown IP'} / {session.user_agent_hash || 'Unknown browser'}</span>
                      </div>
                      <div>
                        <small>Last seen {formatSettingsDate(session.last_seen_at)}</small>
                        <small>Expires {formatSettingsDate(session.expires_at)}</small>
                      </div>
                      <button
                        className="sd-settings-danger-button"
                        disabled={session.is_current || sessionActionId === session.id}
                        onClick={() => handleRevokeSession(session)}
                        type="button"
                      >
                        {session.is_current ? 'Current' : sessionActionId === session.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          )}
        </article>

        <aside className="sd-panel sd-settings-inspector">
          <div className="sd-panel-header">
            <div>
              <h2>Configuration Snapshot</h2>
              <p>Current values before and after save operations.</p>
            </div>
          </div>
          <div className="sd-settings-inspector-body">
            <SummaryRow icon="clock" label="Selected Timezone" value={selectedTimezone?.label || selectedTz} />
            <SummaryRow icon="settings" label="Stored Timezone" value={timezone} />
            <SummaryRow icon="heartbeat" label="Heartbeat" value={`${timeoutDisplay} minutes`} />
            <SummaryRow icon="plan" label="Current Plan" value={planSummary.label} />
            <SummaryRow icon="brand" label="Brand Logo" value={logoUrl ? 'Uploaded' : 'Seedronics default'} />
            <SummaryRow icon="shield" label="2FA" value={twoFaEnabled === true ? 'Enabled' : twoFaEnabled === false ? 'Not enabled' : 'Status unavailable'} />
            <SummaryRow icon="sessions" label="Active Sessions" value={String(activeSessionCount)} />
            <div className={`sd-inline-alert ${hasGeneralChanges ? 'is-warning' : ''}`}>
              {hasGeneralChanges ? 'Save changes to publish these tenant preferences.' : 'Tenant preferences are synced.'}
            </div>
          </div>
        </aside>
      </section>
    </section>
  );
}

type SettingsIconType = 'brand' | 'clock' | 'heartbeat' | 'plan' | 'settings' | 'shield' | 'sessions';

function SummaryRow({ icon, label, value }: { icon?: SettingsIconType; label: string; value: string }) {
  return (
    <div className={`sd-settings-summary-row ${icon ? 'has-icon' : ''}`}>
      {icon && (
        <span className={`sd-settings-summary-icon is-${icon}`}>
          <SettingsIcon type={icon} />
        </span>
      )}
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function SettingsIcon({ type }: { type: SettingsIconType }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" focusable="false">
      {type === 'clock' && (
        <>
          <circle cx="24" cy="24" r="15" />
          <path d="M24 15v10l7 4" />
        </>
      )}
      {type === 'heartbeat' && (
        <>
          <path d="M8 26h7l4-10 8 18 5-11h8" />
          <path d="M14 38h20" />
          <path d="M18 10h12" />
        </>
      )}
      {type === 'brand' && (
        <>
          <circle cx="19" cy="19" r="7" />
          <circle cx="31" cy="18" r="5" />
          <circle cx="29" cy="31" r="7" />
          <path d="M13 33c4-2 8-2 12 0" />
        </>
      )}
      {type === 'shield' && (
        <>
          <path d="M24 8l14 5v10c0 9-5.8 14.8-14 18-8.2-3.2-14-9-14-18V13z" />
          <path d="M18 24l4 4 8-9" />
        </>
      )}
      {type === 'settings' && (
        <>
          <circle cx="24" cy="24" r="5" />
          <path d="M24 8v6" />
          <path d="M24 34v6" />
          <path d="M10.1 16l5.2 3" />
          <path d="M32.7 29l5.2 3" />
          <path d="M10.1 32l5.2-3" />
          <path d="M32.7 19l5.2-3" />
        </>
      )}
      {type === 'plan' && (
        <>
          <rect x="10" y="12" width="28" height="24" rx="5" />
          <path d="M15 20h18" />
          <path d="M15 27h10" />
          <path d="M30 30l3 3 6-8" />
        </>
      )}
      {type === 'sessions' && (
        <>
          <rect x="10" y="12" width="22" height="15" rx="3" />
          <path d="M16 36h16" />
          <path d="M21 27v9" />
          <path d="M34 21h4a4 4 0 0 1 4 4v7" />
          <path d="M38 36h.01" />
        </>
      )}
    </svg>
  );
}

function getSettingsPlanSummary(
  tenantPlan: TenantPlan | null,
  trialStatus: TenantTrialStatus | null,
  loading: boolean
) {
  if (loading) {
    return {
      status: 'loading',
      label: 'Loading plan',
      detail: 'Checking this workspace plan and trial state.',
      metaLabel: 'Trial',
      metaValue: '--',
    };
  }

  const planSlug = getSettingsEffectivePlanSlug(tenantPlan, trialStatus).toLowerCase();
  const basePlanSlug = getSettingsBasePlanSlug(tenantPlan, trialStatus).toLowerCase();
  const planLabel = tenantPlan?.plan_name || formatSettingsPlanTitle(planSlug || 'plan');

  if (trialStatus?.is_trial) {
    const days = trialStatus.days_remaining;
    const trialPlanLabel = formatSettingsPlanTitle(planSlug || trialStatus.trial_plan_slug || 'trial plan');
    const basePlanLabel = formatSettingsPlanTitle(basePlanSlug || 'free');
    return {
      status: 'trial',
      label: `${trialPlanLabel} trial`,
      detail: trialStatus.trial_ends_at
        ? `Trial ends on ${formatSettingsDate(trialStatus.trial_ends_at)}. ${basePlanLabel} resumes afterward unless the tenant upgrades.`
        : `${basePlanLabel} remains the fallback plan after trial.`,
      metaLabel: 'Days remaining',
      metaValue: `${days} day${days === 1 ? '' : 's'}`,
    };
  }

  if (trialStatus?.trial_ends_at && trialStatus.days_remaining <= 0) {
    return {
      status: 'ended',
      label: `${formatSettingsPlanTitle(basePlanSlug || planSlug || 'free')} plan`,
      detail: `Trial ended on ${formatSettingsDate(trialStatus.trial_ends_at)}.`,
      metaLabel: 'Trial',
      metaValue: 'Ended',
    };
  }

  if (planSlug === 'free' || planSlug === 'trial') {
    return {
      status: 'free',
      label: 'Free plan',
      detail: 'This workspace is on the free starting plan.',
      metaLabel: 'Trial',
      metaValue: 'Inactive',
    };
  }

  return {
    status: 'paid',
    label: `${planLabel} plan`,
    detail: 'This workspace is not currently in an active trial.',
    metaLabel: 'Trial',
    metaValue: 'Inactive',
  };
}

function getSettingsEffectivePlanSlug(tenantPlan: TenantPlan | null, trialStatus?: TenantTrialStatus | null) {
  return trialStatus?.effective_plan || trialStatus?.current_plan || tenantPlan?.effective_plan || tenantPlan?.plan || '';
}

function getSettingsBasePlanSlug(tenantPlan: TenantPlan | null, trialStatus?: TenantTrialStatus | null) {
  return trialStatus?.base_plan || tenantPlan?.base_plan || tenantPlan?.plan || '';
}

function formatSettingsPlanTitle(value: string) {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatSettingsDate(value?: string | null) {
  if (!value) return 'unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function isExpiredSession(value?: string | null) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}
